/**
 * Reading an `app.bsky.feed.module` record.
 *
 * A module is addressed two ways — by its own AT-URI, and through the post that
 * carries it — and the two reach the *same* record from opposite directions. So
 * the record is read into one shape here, once, and every reader uses it: a card
 * that had to know which direction it came from would drift the moment a field is
 * added.
 *
 * Nothing in here validates, and that is deliberate. A record written by an older
 * build, or by hand, is still a module as far as the app is concerned: what it
 * cannot say it does not contribute, which is why every field is optional and the
 * playlist comes back as a list that may be empty. Refusing the record would turn
 * one missing field into a module that has vanished from a creator's own profile.
 */
import {type ModuleItem} from '#/lib/modulePlaylist'

/** A module record, as every reader of one wants it. */
export type ModuleRecordView = {
  uri: string
  cid: string
  title: string
  description: string
  /** `draft` while the playlist is still being assembled; `published` after. */
  status: 'draft' | 'published'
  /** The playlist, in order. May be empty; may name a capsule that is gone. */
  items: ModuleItem[]
  /** The grade code the creator chose (`Level_A5`), not a name. */
  level?: string
  subject?: string
  /** The post this module was published as. Absent for a draft. */
  postUri?: string
  postCid?: string
  /** `classroom` when the module was written for one room. */
  visibility?: string
  createdAt: string
  updatedAt?: string
}

/** A string that is worth reading, or nothing. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * A module's address, as a URL path hands it over.
 *
 * A module is addressed by its record — `at://did:.../app.bsky.feed.module/rkey` —
 * and that is what every reader needs: the repo it lives in, the collection, the
 * record key. The one place it arrives in a different form is a *URL*, where a
 * deep link has to be a single path segment and so is percent-encoded
 * (`/module/at%3A%2F%2F...`). `new AtUri(...)` throws on that, and a throw inside a
 * query function is a failed request that never left the device — the module screen
 * said "could not be loaded" for a record that was perfectly readable.
 *
 * So the boundary decodes once, here, rather than every reader of the address
 * remembering to. Decoding is a no-op for a real AT-URI (they contain no `%`), and a
 * value that cannot be decoded is passed through unchanged: a malformed address is
 * then *reported* by whoever reads it, which is a better failure than one invented at
 * the door.
 */
export function moduleUriFromPath(
  value: string | undefined,
): string | undefined {
  if (!value) return undefined
  if (!value.includes('%')) return value
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * The playlist out of a record, item by item.
 *
 * An item with no `uri` is dropped rather than kept as an empty place: a module
 * is a list of capsules, and a place with no capsule in it is a place nothing can
 * be done with — not a missing capsule, which is an item whose *record* cannot be
 * read and which the studio must therefore still be able to show and remove.
 */
export function moduleItemsOfValue(value: unknown): ModuleItem[] {
  if (!Array.isArray(value)) return []
  const items: ModuleItem[] = []
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue
    const uri = text((entry as {uri?: unknown}).uri)
    if (!uri) continue
    const cid = text((entry as {cid?: unknown}).cid)
    const postUri = text((entry as {postUri?: unknown}).postUri)
    items.push({
      uri,
      ...(cid ? {cid} : null),
      ...(postUri ? {postUri} : null),
    })
  }
  return items
}

/** A module record read into the shape everything else reads. */
export function moduleViewOf(
  uri: string,
  cid: string,
  value: Record<string, unknown>,
): ModuleRecordView {
  const post = value.post as {uri?: unknown; cid?: unknown} | undefined
  const postUri = text(post?.uri)
  const postCid = text(post?.cid)
  return {
    uri,
    cid,
    title: text(value.title),
    description: text(value.description),
    status: value.status === 'draft' ? 'draft' : 'published',
    items: moduleItemsOfValue(value.items),
    level: text(value.level) || undefined,
    subject: text(value.subject) || undefined,
    ...(postUri ? {postUri} : null),
    ...(postCid ? {postCid} : null),
    visibility: text(value.visibility) || undefined,
    createdAt: text(value.createdAt),
    updatedAt: text(value.updatedAt) || undefined,
  }
}
